import {
  Box,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { useQuery } from '@tanstack/react-query';
import { individualResultsApi } from '../../api/individualResultsApi';
import { formatTime } from '../../utils/timeFormat';
import type { Discipline, IndividualRankingItem } from '../../types/domain';
import { ATHLETE_AGE_CATEGORY_LABELS } from '../../types/domain';

interface Props {
  discipline: Discipline;
  onEdit?: (item: IndividualRankingItem) => void;
}

export function IndividualRankingTable({ discipline, onEdit }: Props) {
  const { data, isLoading } = useQuery({
    queryKey: ['individual-ranking', discipline.id],
    queryFn: () => individualResultsApi.getIndividualRanking(discipline.id),
    refetchInterval: 5_000,
  });

  if (isLoading) return <Typography>Načítám…</Typography>;
  const items = data ?? [];
  const attempts = discipline.attemptsCount ?? 1;
  const fmt = discipline.timeFormat ?? 'SS_CC';

  return (
    <Paper variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Pořadí</TableCell>
            <TableCell>Závodník</TableCell>
            <TableCell>Družstvo</TableCell>
            <TableCell>Roč.</TableCell>
            <TableCell>Kategorie</TableCell>
            {Array.from({ length: attempts }, (_, i) => (
              <TableCell key={i} align="right">
                Pokus {i + 1}
              </TableCell>
            ))}
            <TableCell align="right">Lepší čas</TableCell>
            <TableCell>Poznámka</TableCell>
            {onEdit && <TableCell align="right">Akce</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((it) => (
            <TableRow key={it.athleteId} hover>
              <TableCell>{it.rank == null ? '—' : `${it.rank}.`}</TableCell>
              <TableCell>
                {it.lastName} {it.firstName}
              </TableCell>
              <TableCell>{it.teamName ?? ''}</TableCell>
              <TableCell>{it.birthYear ?? ''}</TableCell>
              <TableCell>{ATHLETE_AGE_CATEGORY_LABELS[it.ageCategory]}</TableCell>
              {Array.from({ length: attempts }, (_, i) => (
                <TableCell key={i} align="right" sx={{ fontFamily: 'monospace' }}>
                  {it.attemptTimesMs[i] != null ? (
                    formatTime(it.attemptTimesMs[i]!, fmt)
                  ) : it.isNp ? (
                    <Box component="span" sx={{ color: 'error.main' }}>
                      NP
                    </Box>
                  ) : (
                    '—'
                  )}
                </TableCell>
              ))}
              <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                {it.bestTimeMs != null ? (
                  formatTime(it.bestTimeMs, fmt)
                ) : it.isNp ? (
                  <Box component="span" sx={{ color: 'error.main' }}>
                    NP
                  </Box>
                ) : (
                  '—'
                )}
              </TableCell>
              <TableCell sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                {it.note ?? ''}
              </TableCell>
              {onEdit && (
                <TableCell align="right">
                  <IconButton size="small" onClick={() => onEdit(it)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              )}
            </TableRow>
          ))}
          {items.length === 0 && (
            <TableRow>
              <TableCell colSpan={20} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                Žádné výsledky
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Paper>
  );
}
